import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, getDocs, collection, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { COLORS } from '../theme';

const SUSPENSION_TYPES = [
  { value: 'three_strikes', label: 'Three strikes (contact violations)' },
  { value: 'fraud', label: 'Fraud or scam' },
  { value: 'inappropriate_behavior', label: 'Inappropriate behavior' },
  { value: 'spam', label: 'Spam or abuse' },
  { value: 'security_risk', label: 'Security risk' },
  { value: 'accidental', label: 'Accidental — undo' },
  { value: 'other', label: 'Other' },
];

const REACTIVATION_TYPES = [
  { value: 'issue_resolved', label: 'Issue resolved' },
  { value: 'suspended_in_error', label: 'Suspended in error' },
  { value: 'corrected_behavior', label: 'Worker corrected behavior' },
  { value: 'investigation_cleared', label: 'Investigation cleared' },
  { value: 'strikes_forgiven', label: 'Strikes forgiven' },
  { value: 'other', label: 'Other' },
];

const SUSPENSION_TYPE_LABELS = Object.fromEntries(SUSPENSION_TYPES.map(t => [t.value, t.label]));

// Modal copy per action mode. 'clear_warning' calls the same
// adminReactivateAccount callable as 'reactivate' (any non-'suspend' mode
// does — see submitModal()) but gets its own title/button/toast wording
// since "Reactivate" implies undoing a suspension, which a warned-only
// account never had.
const MODAL_COPY = {
  suspend: { title: 'Suspend Account', confirmLabel: 'Suspend', toast: 'Account suspended' },
  reactivate: { title: 'Reactivate Account', confirmLabel: 'Reactivate', toast: 'Account reactivated' },
  clear_warning: { title: 'Clear Warning', confirmLabel: 'Clear Warning', toast: 'Warning cleared' },
};

const INPUT_STYLE = {
  color: '#1F2937',
  backgroundColor: 'white',
  border: '1px solid #D1D5DB',
  padding: '8px 12px',
  borderRadius: '6px',
  fontSize: '14px',
  width: '100%',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};

function StatusBadge({ status }) {
  const map = {
    active: { bg: '#34A85326', text: COLORS.success, label: 'Active' },
    suspended: { bg: '#EA433526', text: COLORS.danger, label: 'Suspended' },
    warned: { bg: '#F5A62326', text: COLORS.warning, label: 'Warned' },
    pending_email_verification: { bg: '#F5A62326', text: COLORS.warning, label: 'Pending' },
  };
  const s = map[status] || { bg: COLORS.surfaceHover, text: COLORS.textSecondary, label: status || '—' };
  return (
    <span style={{ backgroundColor: s.bg, color: s.text, borderRadius: '4px', padding: '2px 8px', fontSize: '11px', fontWeight: '600' }}>
      {s.label}
    </span>
  );
}

function AppStatusBadge({ status }) {
  const map = {
    pending:    { bg: '#fef3c7', text: '#92400e' },
    reviewing:  { bg: '#dbeafe', text: '#1e40af' },
    send_offer: { bg: '#ede9fe', text: '#6d28d9' },
    offer_sent: { bg: '#ccfbf1', text: '#0f766e' },
    hired:      { bg: '#d1fae5', text: '#065f46' },
    rejected:   { bg: '#fee2e2', text: '#991b1b' },
  };
  const s = map[status] || { bg: COLORS.surfaceHover, text: COLORS.textSecondary };
  return (
    <span style={{ backgroundColor: s.bg, color: s.text, borderRadius: '4px', padding: '2px 8px', fontSize: '11px', fontWeight: '600' }}>
      {status || '—'}
    </span>
  );
}

function AiScoreBadge({ label }) {
  if (!label) return <span style={{ color: COLORS.textMuted, fontSize: '12px' }}>—</span>;
  const map = {
    'strong': { bg: '#D1FAE5', text: '#065F46' },
    'good':   { bg: '#DBEAFE', text: '#1E40AF' },
    'weak':   { bg: '#FEF3C7', text: '#92400E' },
  };
  const s = map[label] || { bg: '#F3F4F6', text: '#6B7280' };
  return (
    <span style={{ backgroundColor: s.bg, color: s.text, borderRadius: '4px', padding: '2px 8px', fontSize: '11px', fontWeight: '600' }}>
      {label}
    </span>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={{
      flex: 1,
      backgroundColor: COLORS.surface,
      border: `1px solid ${COLORS.border}`,
      borderRadius: '8px',
      padding: '16px',
      textAlign: 'center',
    }}>
      <div style={{ fontSize: '26px', fontWeight: '700', color: COLORS.accent, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '11px', color: COLORS.textSecondary, marginTop: '6px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>{label}</div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '13px', color: value ? COLORS.textPrimary : COLORS.textMuted }}>{value || '—'}</div>
    </div>
  );
}

function formatDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function WorkerDetailPage() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [modal, setModal] = useState(null); // { mode: 'suspend' | 'reactivate' | 'clear_warning' }
  const [modalType, setModalType] = useState('');
  const [modalReason, setModalReason] = useState('');
  const [modalPassword, setModalPassword] = useState('');
  const [modalError, setModalError] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    async function load() {
      const snap = await getDoc(doc(db, 'users', uid));
      if (!snap.exists()) { setNotFound(true); setLoading(false); return; }
      setUser({ id: snap.id, ...snap.data() });

      const appsSnap = await getDocs(query(collection(db, 'applications'), where('userId', '==', uid)));
      setApplications(appsSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.appliedAt?.toMillis?.() || 0) - (a.appliedAt?.toMillis?.() || 0)));

      setLoading(false);
    }
    load();
  }, [uid]);

  async function reloadUser() {
    const snap = await getDoc(doc(db, 'users', uid));
    if (snap.exists()) setUser({ id: snap.id, ...snap.data() });
  }

  function openModal(mode) {
    setModal({ mode });
    setModalType('');
    setModalReason('');
    setModalPassword('');
    setModalError('');
  }

  async function submitModal() {
    if (!modalType) { setModalError('Please select a reason.'); return; }
    if (modalReason.trim().length < 10) { setModalError('Details must be at least 10 characters.'); return; }
    if (!modalPassword) { setModalError('Password is required.'); return; }
    setModalLoading(true);
    setModalError('');
    try {
      if (modal.mode === 'suspend') {
        const fn = httpsCallable(functions, 'adminSuspendAccount');
        await fn({ targetUid: uid, password: modalPassword, suspensionType: modalType, suspensionReason: modalReason.trim() });
      } else {
        const fn = httpsCallable(functions, 'adminReactivateAccount');
        await fn({ targetUid: uid, password: modalPassword, reactivationType: modalType, reactivationReason: modalReason.trim() });
      }
      setToast(MODAL_COPY[modal.mode].toast);
      setTimeout(() => setToast(''), 3000);
      setModal(null);
      await reloadUser();
    } catch (err) {
      setModalPassword('');
      setModalError(err.message || 'Action failed. Check your password and try again.');
    } finally {
      setModalLoading(false);
    }
  }

  if (loading) return <div style={{ color: COLORS.textSecondary, fontSize: '13px' }}>Loading…</div>;
  if (notFound) return <div style={{ color: COLORS.danger, fontSize: '13px' }}>Worker not found.</div>;

  const profile = user.profile || {};
  const skills = Array.isArray(profile.skills) ? profile.skills.join(', ') : profile.skills || null;
  const hiredCount = applications.filter(a => a.status === 'hired').length;
  const strikes = user.contactViolations ?? 0;
  const rating = profile.rating ? profile.rating.toFixed(1) : '—';
  const typeOptions = modal?.mode === 'suspend' ? SUSPENSION_TYPES : REACTIVATION_TYPES;

  const card = { backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: '8px', padding: '20px' };
  const sectionLabel = { fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', marginBottom: '16px' };
  const fieldLabel = { fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', display: 'block', marginBottom: '6px' };

  return (
    <div>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
          backgroundColor: COLORS.success, color: 'white',
          padding: '10px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: '500',
          zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        }}>
          {toast}
        </div>
      )}

      {/* Suspend / Reactivate / Clear Warning Modal */}
      {modal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: COLORS.surface, borderRadius: '12px', padding: '28px',
            width: '420px', boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          }}>
            <div style={{ fontSize: '16px', fontWeight: '700', color: COLORS.textPrimary, marginBottom: '6px' }}>
              {MODAL_COPY[modal.mode].title}
            </div>
            <div style={{ fontSize: '13px', color: COLORS.textSecondary, marginBottom: '20px' }}>
              Re-enter your password and provide a reason. This action will be logged.
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={fieldLabel}>Reason</label>
              <select value={modalType} onChange={e => setModalType(e.target.value)} style={INPUT_STYLE}>
                <option value="">Select a reason…</option>
                {typeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={fieldLabel}>Details</label>
              <textarea
                value={modalReason}
                onChange={e => setModalReason(e.target.value)}
                placeholder="Minimum 10 characters"
                rows={3}
                style={{ ...INPUT_STYLE, resize: 'vertical' }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={fieldLabel}>Your Password</label>
              <input
                type="password"
                autoComplete="current-password"
                value={modalPassword}
                onChange={e => setModalPassword(e.target.value)}
                style={INPUT_STYLE}
              />
            </div>

            {modalError && (
              <div style={{ fontSize: '12px', color: COLORS.danger, marginBottom: '12px' }}>
                {modalError}
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setModal(null)}
                disabled={modalLoading}
                style={{
                  padding: '8px 16px', fontSize: '13px', fontWeight: '500',
                  border: `1px solid ${COLORS.border}`, borderRadius: '6px',
                  backgroundColor: 'transparent', color: COLORS.textSecondary, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitModal}
                disabled={modalLoading}
                style={{
                  padding: '8px 16px', fontSize: '13px', fontWeight: '500',
                  border: 'none', borderRadius: '6px', cursor: 'pointer',
                  backgroundColor: modal.mode === 'suspend' ? COLORS.danger : COLORS.success,
                  color: 'white',
                  opacity: modalLoading ? 0.6 : 1,
                }}
              >
                {modalLoading ? '…' : MODAL_COPY[modal.mode].confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Back + action */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <button
          onClick={() => navigate('/workers')}
          style={{ background: 'none', border: 'none', color: COLORS.accent, fontSize: '13px', cursor: 'pointer', padding: 0, fontWeight: '500' }}
        >
          ← Workers
        </button>
        <div style={{ display: 'flex', gap: '8px' }}>
          {user.accountStatus === 'suspended' && (
            <button
              onClick={() => openModal('reactivate')}
              style={{
                backgroundColor: COLORS.success, color: 'white',
                border: 'none', borderRadius: '6px',
                padding: '7px 16px', fontSize: '13px', fontWeight: '500', cursor: 'pointer',
              }}
            >
              Reactivate Account
            </button>
          )}
          {/* HC-EMPLOYER-FREEZE item 6, phase 1 — manual clear only, no
              automatic/time-based expiry. Distinct from Reactivate (which is
              specifically for undoing a suspension) — a warned account was
              never suspended, so it gets its own clearly-labeled action
              rather than reusing "Reactivate" wording. Shown alongside
              Suspend (below) since a warned worker is a legitimate target for
              either: clear the warning if resolved, or escalate if not. */}
          {user.accountStatus === 'warned' && (
            <button
              onClick={() => openModal('clear_warning')}
              style={{
                backgroundColor: COLORS.success, color: 'white',
                border: 'none', borderRadius: '6px',
                padding: '7px 16px', fontSize: '13px', fontWeight: '500', cursor: 'pointer',
              }}
            >
              Clear Warning
            </button>
          )}
          {(user.accountStatus === 'active' || user.accountStatus === 'warned') && (
            <button
              onClick={() => openModal('suspend')}
              style={{
                backgroundColor: COLORS.danger, color: 'white',
                border: 'none', borderRadius: '6px',
                padding: '7px 16px', fontSize: '13px', fontWeight: '500', cursor: 'pointer',
              }}
            >
              Suspend Account
            </button>
          )}
        </div>
      </div>

      {/* Name + email */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '22px', fontWeight: '700', color: COLORS.textPrimary }}>{user.displayName || user.email || '(No name)'}</div>
        <div style={{ fontSize: '13px', color: COLORS.textSecondary, marginTop: '4px' }}>{user.email || '—'}</div>
      </div>

      {/* Stats row */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
        <StatCard label="Applications" value={applications.length} />
        <StatCard label="Hired" value={hiredCount} />
        <StatCard label="Strikes" value={strikes} />
        <StatCard label="Rating" value={rating} />
      </div>

      {/* Two-column grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '20px', alignItems: 'start' }}>
        {/* Left: profile info */}
        <div style={card}>
          <div style={sectionLabel}>Profile</div>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', marginBottom: '6px' }}>Status</div>
            <StatusBadge status={user.accountStatus} />
            {profile.available !== undefined && (
              <span style={{
                marginLeft: '6px',
                backgroundColor: profile.available ? '#34A85326' : COLORS.surfaceHover,
                color: profile.available ? COLORS.success : COLORS.textMuted,
                borderRadius: '4px',
                padding: '2px 8px',
                fontSize: '11px',
                fontWeight: '600',
              }}>
                {profile.available ? 'Available' : 'Unavailable'}
              </span>
            )}
          </div>

          {user.accountStatus === 'suspended' && (
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#EA433510', borderRadius: '6px', border: '1px solid #EA433330' }}>
              <div style={{ fontSize: '11px', color: COLORS.danger, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', marginBottom: '10px' }}>
                Suspension Details
              </div>
              <div style={{ marginBottom: '8px' }}>
                <div style={{ fontSize: '11px', color: COLORS.textSecondary, fontWeight: '600', marginBottom: '2px' }}>Reason</div>
                <div style={{ fontSize: '12px', color: COLORS.textPrimary }}>{SUSPENSION_TYPE_LABELS[user.suspensionType] || user.suspensionType || '—'}</div>
              </div>
              <div style={{ marginBottom: '8px' }}>
                <div style={{ fontSize: '11px', color: COLORS.textSecondary, fontWeight: '600', marginBottom: '2px' }}>Details</div>
                <div style={{ fontSize: '12px', color: COLORS.textPrimary }}>{user.suspensionReason || '—'}</div>
              </div>
              <div style={{ marginBottom: '8px' }}>
                <div style={{ fontSize: '11px', color: COLORS.textSecondary, fontWeight: '600', marginBottom: '2px' }}>Suspended by</div>
                <div style={{ fontSize: '12px', color: COLORS.textPrimary }}>
                  {user.suspendedBy === 'system' ? 'System (auto)' : (user.suspendedByEmail || user.suspendedBy || '—')}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: COLORS.textSecondary, fontWeight: '600', marginBottom: '2px' }}>Suspended at</div>
                <div style={{ fontSize: '12px', color: COLORS.textPrimary }}>{formatDate(user.suspendedAt)}</div>
              </div>
            </div>
          )}

          <Field label="Language" value={user.appLanguage} />
          <Field label="Skills" value={skills} />
          <Field label="Member Since" value={formatDate(user.createdAt)} />
          <div style={{ marginBottom: 0 }}>
            <div style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', marginBottom: '4px' }}>UID</div>
            <div style={{ fontSize: '11px', color: COLORS.textMuted, wordBreak: 'break-all', fontFamily: 'monospace' }}>{uid}</div>
          </div>
        </div>

        {/* Right: applications table */}
        <div style={card}>
          <div style={sectionLabel}>Applications ({applications.length})</div>
          {applications.length === 0 ? (
            <div style={{ color: COLORS.textMuted, fontSize: '13px' }}>No applications.</div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 120px 110px', borderBottom: `1px solid ${COLORS.border}`, paddingBottom: '8px', marginBottom: '4px' }}>
                {['Job', 'Status', 'AI Score', 'Applied'].map(h => (
                  <div key={h} style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>{h}</div>
                ))}
              </div>
              {applications.map((a, i) => (
                <div key={a.id} style={{ display: 'grid', gridTemplateColumns: '1fr 110px 120px 110px', alignItems: 'center', padding: '10px 0', borderBottom: i < applications.length - 1 ? `1px solid ${COLORS.border}30` : 'none' }}>
                  <div style={{ fontSize: '13px', color: COLORS.textPrimary, fontWeight: '500' }}>{a.jobTitle || '—'}</div>
                  <div><AppStatusBadge status={a.status} /></div>
                  <div><AiScoreBadge label={a.aiLabel} /></div>
                  <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>{formatDate(a.appliedAt)}</div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
